import { useState, useEffect, useMemo, useCallback } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { AppCard } from '@/features/project/components/app-card'
import { toast } from '@/store/toast-store'
import { handleProjectCreateError } from '@/store/project-create-error'
import { Folder, Globe, ExternalLink, ChevronDown, ChevronUp, MessageCircle, Pencil, Settings, UploadCloud } from 'lucide-react'
import { AppAnalytics } from '../components/app-analytics'
import { ProjectCardStatusFrame } from '../components/project-card-status-frame'
import type { ProjectMeta } from '@/services/project-api'
import { fetchDeployedApps, updateAppSettings, deleteDeployedApp, fetchDeployedAppJson, updateDeployedAppTier, saveProjectMeta } from '@/services/project-api'
import type { DeployedAppInfo } from '@/services/project-api'
import { upgradeToTierA } from '../components/tier-upgrade'
import { DashboardContainer } from '../components/dashboard-container'
import { useStudioLocale } from '@/i18n'
import {
  STUDIO_DICTIONARIES,
  type StudioDictionary,
} from '@/i18n/studio-dictionaries'
import { pushStudioPath, pushStudioRoute } from '@/lib/studio-navigation'
import { cogoXyzRegistry } from '@/features/cogo-xyz/cogo-xyz-registry'
import {
  getProjectProofHealth,
  type ProjectProofHealth,
  type ProjectProofHealthAction,
  type ProjectProofHealthTone,
} from '@/features/project/lib/project-proof-health'
import { AppThumbnailPreview } from '@/features/project/components/app-thumbnail-preview'
import { openStudioLaunchChat } from '../lib/studio-launch-chat'
import { libraryItemSourceProjectId } from './library-project-association'

const PROJECT_BATCH_SIZE = 24

type AgentShareCopy = {
  selected: string
  open: string
  manage: string
  ask: string
  select: string
  selectedLabel: string
  readinessDetails: string
  readinessDetailsHint: string
  outputDetails: string
  sharedSurface: string
  publicOutput: string
  draftOutput: string
  sharedStatus: string
  draftStatus: string
  version: string
  source: string
  sourceBundle: string
  readiness: string
  nextAction: string
  savedRecords: string
  openInStudio: string
  remove: string
  upgrade: string
  upgrading: string
  analytics: string
  blocker: string
  needsVerification: string
  needsVerificationStatus: string
  needsVerificationCount: (count: number) => string
  openNamed: (name: string) => string
  sharedVersion: (version: number) => string
  askNotice: (name: string, label: string, detail: string) => string
  externalPublish: string
  externalPublishBoundary: string
}

const AGENT_SHARE_COPY = {
  en: {
    selected: 'Selected output',
    open: 'Open output',
    manage: 'Manage sharing',
    ask: 'Ask Coogo about sharing readiness',
    select: 'Select this output',
    selectedLabel: 'Selected',
    readinessDetails: 'Share readiness details',
    readinessDetailsHint: 'Checks, evidence, provenance, and management actions',
    outputDetails: 'Output details',
    sharedSurface: 'Release status',
    publicOutput: 'Public output',
    draftOutput: 'Draft output',
    sharedStatus: 'Shared',
    draftStatus: 'Draft',
    version: 'Version',
    source: 'Source',
    sourceBundle: 'cogo source bundle',
    readiness: 'Readiness check',
    nextAction: 'Next action',
    savedRecords: 'Saved records',
    openInStudio: 'Open in Studio',
    remove: 'Remove',
    upgrade: 'Upgrade plan',
    upgrading: 'Upgrading…',
    analytics: 'Analytics',
    blocker: 'Before sharing',
    needsVerification: 'Needs verification',
    needsVerificationStatus: 'Verification needed',
    needsVerificationCount: (count) => `${count} need verification`,
    openNamed: (name) => `Open ${name}`,
    sharedVersion: (version) => `shared v${version}`,
    askNotice: (name, label, detail) =>
      `${name} sharing readiness is “${label}.” ${detail} Tell me what to check or change before sharing, in priority order.`,
    externalPublish: 'External Cogo publish',
    externalPublishBoundary: 'This cogo.xyz link is not installed in this DGX workspace. Keep it as an external publication until a DGX release receipt exists.',
  },
  ko: {
    selected: '선택한 결과물',
    open: '결과물 열기',
    manage: '공유 관리',
    ask: '공유 준비를 Coogo에게 묻기',
    select: '이 결과물 선택',
    selectedLabel: '선택됨',
    readinessDetails: '공유 준비 상세',
    readinessDetailsHint: '점검, 증거, 출처와 관리 작업 보기',
    outputDetails: '결과물별 상세',
    sharedSurface: '공개 상태',
    publicOutput: '공개 결과물',
    draftOutput: '초안 결과물',
    sharedStatus: '공유됨',
    draftStatus: '초안',
    version: '버전',
    source: '소스',
    sourceBundle: 'cogo 소스 번들',
    readiness: '준비 상태',
    nextAction: '다음 작업',
    savedRecords: '저장된 기록',
    openInStudio: 'Studio에서 열기',
    remove: '제거',
    upgrade: '플랜 업그레이드',
    upgrading: '업그레이드 중…',
    analytics: '분석',
    blocker: '공유 전 확인',
    needsVerification: '검증 필요',
    needsVerificationStatus: '검증 필요',
    needsVerificationCount: (count) => `${count}건 검증 필요`,
    openNamed: (name) => `${name} 열기`,
    sharedVersion: (version) => `공유됨 v${version}`,
    askNotice: (name, label, detail) =>
      `${name}의 공유 준비 상태는 “${label}”입니다. ${detail} 공유 전에 확인하거나 바꿔야 할 항목을 우선순위와 함께 알려 주세요.`,
    externalPublish: '외부 Cogo 발행',
    externalPublishBoundary: '이 cogo.xyz 링크는 현재 DGX 워크스페이스에 설치된 결과가 아닙니다. DGX 릴리스 영수증이 생기기 전까지 외부 발행으로 구분합니다.',
  },
  ja: {
    selected: '選択中の成果物',
    open: '成果物を開く',
    manage: '共有を管理',
    ask: '共有準備について Coogo に聞く',
    select: 'この成果物を選択',
    selectedLabel: '選択中',
    readinessDetails: '共有準備の詳細',
    readinessDetailsHint: '点検、証拠、出所、管理操作を表示',
    outputDetails: '成果物ごとの詳細',
    sharedSurface: '公開状態',
    publicOutput: '公開中の成果物',
    draftOutput: '下書きの成果物',
    sharedStatus: '共有済み',
    draftStatus: '下書き',
    version: 'バージョン',
    source: 'ソース',
    sourceBundle: 'cogo ソースバンドル',
    readiness: '準備状況',
    nextAction: '次の操作',
    savedRecords: '保存済みの記録',
    openInStudio: 'Studio で開く',
    remove: '削除',
    upgrade: 'プランをアップグレード',
    upgrading: 'アップグレード中…',
    analytics: '分析',
    blocker: '共有前の確認',
    needsVerification: '検証が必要',
    needsVerificationStatus: '検証が必要',
    needsVerificationCount: (count) => `${count} 件は検証が必要`,
    openNamed: (name) => `${name} を開く`,
    sharedVersion: (version) => `共有 v${version}`,
    askNotice: (name, label, detail) =>
      `${name} の共有準備状況は「${label}」です。${detail} 共有前に確認または変更すべき項目を優先順位付きで教えてください。`,
    externalPublish: '外部 Cogo 公開',
    externalPublishBoundary: 'この cogo.xyz リンクは、この DGX ワークスペースにインストールされた成果物ではありません。DGX リリース領収書が得られるまでは外部公開として扱います。',
  },
  zh: {
    selected: '已选成果',
    open: '打开成果',
    manage: '管理共享',
    ask: '向 Coogo 询问共享准备情况',
    select: '选择此成果',
    selectedLabel: '已选择',
    readinessDetails: '共享准备详情',
    readinessDetailsHint: '查看检查、证据、来源和管理操作',
    outputDetails: '各成果详情',
    sharedSurface: '发布状态',
    publicOutput: '公开成果',
    draftOutput: '草稿成果',
    sharedStatus: '已共享',
    draftStatus: '草稿',
    version: '版本',
    source: '来源',
    sourceBundle: 'cogo 来源包',
    readiness: '准备状态',
    nextAction: '下一步',
    savedRecords: '已保存记录',
    openInStudio: '在 Studio 中打开',
    remove: '删除',
    upgrade: '升级套餐',
    upgrading: '正在升级…',
    analytics: '分析',
    blocker: '共享前确认',
    needsVerification: '需要验证',
    needsVerificationStatus: '需要验证',
    needsVerificationCount: (count) => `${count} 项需要验证`,
    openNamed: (name) => `打开 ${name}`,
    sharedVersion: (version) => `已共享 v${version}`,
    askNotice: (name, label, detail) =>
      `${name} 的共享准备状态为“${label}”。${detail} 请按优先级说明共享前需要检查或更改的项目。`,
    externalPublish: '外部 Cogo 发布',
    externalPublishBoundary: '此 cogo.xyz 链接尚未安装到当前 DGX 工作区。在获得 DGX 发布凭证之前，请将其视为外部发布。',
  },
  'zh-TW': {
    selected: '已選成果',
    open: '開啟成果',
    manage: '管理分享',
    ask: '向 Coogo 詢問分享準備狀態',
    select: '選擇此成果',
    selectedLabel: '已選取',
    readinessDetails: '分享準備詳情',
    readinessDetailsHint: '查看檢查、證據、來源與管理操作',
    outputDetails: '各成果詳情',
    sharedSurface: '發佈狀態',
    publicOutput: '公開成果',
    draftOutput: '草稿成果',
    sharedStatus: '已分享',
    draftStatus: '草稿',
    version: '版本',
    source: '來源',
    sourceBundle: 'cogo 來源套件',
    readiness: '準備狀態',
    nextAction: '下一步',
    savedRecords: '已儲存記錄',
    openInStudio: '在 Studio 中開啟',
    remove: '移除',
    upgrade: '升級方案',
    upgrading: '正在升級…',
    analytics: '分析',
    blocker: '分享前確認',
    needsVerification: '需要驗證',
    needsVerificationStatus: '需要驗證',
    needsVerificationCount: (count) => `${count} 項需要驗證`,
    openNamed: (name) => `開啟 ${name}`,
    sharedVersion: (version) => `已分享 v${version}`,
    askNotice: (name, label, detail) =>
      `${name} 的分享準備狀態為「${label}」。${detail} 請依優先順序說明分享前需要檢查或變更的項目。`,
    externalPublish: '外部 Cogo 發佈',
    externalPublishBoundary: '此 cogo.xyz 連結尚未安裝到目前的 DGX 工作區。在取得 DGX 發佈憑證前，請將其視為外部發佈。',
  },
} satisfies Record<keyof typeof STUDIO_DICTIONARIES, AgentShareCopy>

type WorkLibraryRelationshipCopy = {
  summary: (projects: number, items: number) => string
  editableWorkspace: string
  savedItems: (count: number) => string
  notSaved: string
}

const WORK_LIBRARY_RELATIONSHIP_COPY = {
  en: {
    summary: (projects, items) => `${projects} editable workspaces · ${items} Library records loaded in this session. A project can have zero or more reusable records.`,
    editableWorkspace: 'Editable workspace',
    savedItems: (count) => `${count} loaded Library ${count === 1 ? 'record' : 'records'}`,
    notSaved: 'No loaded Library record',
  },
  ko: {
    summary: (projects, items) => `편집 가능한 워크스페이스 ${projects}개 · 이 세션에서 읽은 Library 기록 ${items}개. 하나의 프로젝트에는 재사용 기록이 0개 이상 연결될 수 있습니다.`,
    editableWorkspace: '편집 가능한 워크스페이스',
    savedItems: (count) => `읽은 Library 기록 ${count}개`,
    notSaved: '읽힌 Library 기록 없음',
  },
  ja: {
    summary: (projects, items) => `編集可能なワークスペース ${projects} 件 · このセッションで読み込んだ Library 記録 ${items} 件。1つのプロジェクトには再利用可能な記録が0件以上紐づきます。`,
    editableWorkspace: '編集可能なワークスペース',
    savedItems: (count) => `読み込み済み Library 記録 ${count} 件`,
    notSaved: '読み込み済み Library 記録なし',
  },
  zh: {
    summary: (projects, items) => `${projects} 个可编辑工作区 · 本次会话已读取 ${items} 个 Library 记录。一个项目可关联零个或多个可复用记录。`,
    editableWorkspace: '可编辑工作区',
    savedItems: (count) => `${count} 个已读取 Library 记录`,
    notSaved: '没有已读取的 Library 记录',
  },
  'zh-TW': {
    summary: (projects, items) => `${projects} 個可編輯工作區 · 本次工作階段已讀取 ${items} 個 Library 記錄。一個專案可連結零個或多個可重用記錄。`,
    editableWorkspace: '可編輯工作區',
    savedItems: (count) => `${count} 個已讀取 Library 記錄`,
    notSaved: '沒有已讀取的 Library 記錄',
  },
} satisfies Record<keyof typeof STUDIO_DICTIONARIES, WorkLibraryRelationshipCopy>

function isExternalCogoPublish(hostname: string | null | undefined): boolean {
  const host = hostname?.trim().toLowerCase()
  return Boolean(host && (host === 'cogo.xyz' || host.endsWith('.cogo.xyz')))
}

function proofHealthBadgeClassName(tone: ProjectProofHealthTone): string {
  if (tone === 'blocked') return 'border border-destructive/30 bg-background text-destructive'
  return 'border border-border bg-background text-muted-foreground'
}

function proofHealthDotClassName(tone: ProjectProofHealthTone): string {
  if (tone === 'ready') return 'bg-foreground'
  if (tone === 'blocked') return 'bg-destructive'
  if (tone === 'review') return 'bg-muted-foreground'
  return 'bg-muted-foreground/50'
}

/**
 * The next-action button's words.
 *
 * Was `(label: string)` branching on `/deploy/i.test(label)` over the English
 * string the health lib returned — so localizing that string would have
 * silently killed the branch and put the raw label on the button, and its
 * second arm (`/proof|readback/`) never matched any value the lib could
 * produce. It now switches on the lib's `action` discriminator and reads its
 * words from the active locale, which is why the copy can be translated
 * without changing what the button does.
 */
function proofActionLabel(
  action: ProjectProofHealthAction,
  copy: StudioDictionary['proofHealth'],
): string {
  return action === 'open-deploy' ? copy.reviewShare : copy.reviewEvidence
}

// ── Share health ────────────────────────────────────────────────────────────
// Reported defect: every word of this panel came from
// `latestStudioProductionHealth` — a constant in
// `features/production-health/studio-production-health.ts` recorded
// 2026-07-02 that describes *coogo's own* studio deployment (www.cogo.xyz and
// its Cloudflare Pages preview). Rendered between "SHARED OUTPUTS 0" and the
// viewer's own output list, it read as a statement about the viewer's shared
// work. It said "Share links verified · 2/2 links reachable" to someone who
// had shared nothing at all, and its two buttons opened coogo's website.
//
// Two independent errors, either one enough on its own:
//   1. Wrong subject — coogo's site, not the viewer's output.
//   2. Wrong tense — a 29-day-old frozen snapshot rendered in the present
//      tense ("are reachable"), with its recorded date demoted to a field
//      beside the claim rather than being part of it.
//
// The panel now reports only what this screen actually reads: the outputs the
// viewer has shared, and the readiness records those outputs carry. A
// confirmation is arithmetically impossible while nothing is shared, because
// every number here is a count of the viewer's own shared outputs. Nothing is
// probed on open, and the copy says so rather than implying otherwise.

export type ShareHealthTone = 'nothing-shared' | 'unchecked' | 'checked' | 'attention'

export interface ShareHealthSummary {
  tone: ShareHealthTone
  /** Pill text. Carries its own subject, count, and — when it exists — date. */
  badge: string
  detail: string
  sharedCount: number
  checkedCount: number
  blockedCount: number
  /** Newest `createdAt` across the shared outputs' passing records. */
  lastCheckedAt: string | null
}

function fillShare(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in values ? values[key] : whole,
  )
}

/**
 * The wording this summary may use. Defaulted to English so the pure function
 * stays directly assertable without a provider; the page passes the reader's
 * own language through.
 */
export type ShareHealthCopy = StudioDictionary['share']['health']

const EN_SHARE_HEALTH_COPY: ShareHealthCopy = STUDIO_DICTIONARIES.en.share.health

function formatShareDate(value: string): string {
  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? new Date(timestamp).toLocaleDateString('ko-KR') : value
}

/**
 * Reduces the readiness records of the viewer's *shared* outputs to one
 * statement. Takes only shared outputs — drafts are not shared, so they can
 * neither confirm nor deny anything about sharing.
 */
export function summarizeShareHealth(
  sharedOutputProof: ProjectProofHealth[],
  copy: ShareHealthCopy = EN_SHARE_HEALTH_COPY,
): ShareHealthSummary {
  const sharedCount = sharedOutputProof.length
  const checkedCount = sharedOutputProof.filter((proof) => proof.status === 'verified').length
  const blockedCount = sharedOutputProof.filter((proof) => proof.status === 'blocked').length
  const lastCheckedAt = sharedOutputProof
    .map((proof) => proof.lastVerifiedAt)
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1) ?? null

  if (sharedCount === 0) {
    return {
      tone: 'nothing-shared',
      badge: copy.nothingSharedBadge,
      detail: copy.nothingSharedDetail,
      sharedCount, checkedCount, blockedCount, lastCheckedAt,
    }
  }

  if (blockedCount > 0) {
    return {
      tone: 'attention',
      badge: fillShare(sharedCount === 1 ? copy.blockedBadgeOne : copy.blockedBadge, {
        blocked: String(blockedCount),
        shared: String(sharedCount),
      }),
      detail: copy.blockedDetail,
      sharedCount, checkedCount, blockedCount, lastCheckedAt,
    }
  }

  if (checkedCount === 0) {
    return {
      tone: 'unchecked',
      badge: fillShare(
        sharedCount === 1 ? copy.uncheckedBadgeOne : copy.uncheckedBadge,
        { shared: String(sharedCount) },
      ),
      detail: copy.uncheckedDetail,
      sharedCount, checkedCount, blockedCount, lastCheckedAt,
    }
  }

  return {
    tone: 'checked',
    badge:
      fillShare(sharedCount === 1 ? copy.checkedBadgeOne : copy.checkedBadge, {
        checked: String(checkedCount),
        shared: String(sharedCount),
      })
      + (lastCheckedAt
        ? fillShare(copy.checkedBadgeLastSuffix, {
            date: formatShareDate(lastCheckedAt),
          })
        : ''),
    detail: copy.checkedDetail,
    sharedCount, checkedCount, blockedCount, lastCheckedAt,
  }
}

// Attention is the only tone that gets a colour. Nothing here earns a
// success/confirmation colour: the panel reports recorded counts, and a colour
// that reads as "all good" would re-introduce the claim the counts avoid.
function shareHealthBadgeClassName(tone: ShareHealthTone): string {
  return tone === 'attention' ? 'border-destructive/30 text-destructive' : 'border-border text-muted-foreground'
}

// ── Load failures ───────────────────────────────────────────────────────────
// Reported defect: a failed *first* read of saved work said "Saved work could
// not be refreshed" — nothing had been refreshed, because nothing had been read
// yet — and put the raw transport string underneath it as the entire
// explanation. Observed on the Work screen with `/api/projects` unreachable:
// the heading claimed a refresh, and the only other words on the banner were
// "Failed to fetch", which names neither a cause the reader recognises nor
// anything they can do.
//
// The two cases really are different and the store already tells them apart:
// `userProjectsLoadedAt` is null until a list has landed for the signed-in
// principal, and is reset when the principal changes. So the copy distinguishes
// them instead of guessing — a first read that never landed, versus a re-read
// that failed while an earlier list is still on screen.
//
// The raw message is kept, because it is what makes a bug report actionable,
// but it is demoted to a labelled technical line under the guidance rather than
// standing in for it.

type LoadFailureCause = 'offline' | 'timeout' | 'auth' | 'server' | 'unknown'

export interface LoadFailureCopy {
  /** What failed, in the tense that actually happened. */
  title: string
  /** Why it failed and what the reader can do next. */
  guidance: string
  /** The verbatim transport/server message, or null when there wasn't one. */
  technicalDetail: string | null
}

/**
 * Classifies a thrown message into the handful of causes a reader can act on.
 * `Failed to fetch` is what a browser throws for offline, DNS failure, a
 * refused connection, and a blocked request alike — all of which the reader
 * addresses the same way, so they share one bucket.
 *
 * An HTTP status code is checked FIRST, because a status code means the server
 * answered. `fetchDeployedApps` throws `Failed to fetch apps: 503`, which
 * collides word-for-word with the browser's transport error while meaning the
 * opposite of it; the status code is the part that is unambiguous.
 */
function classifyLoadFailure(message: string | null): LoadFailureCause {
  if (!message) return 'unknown'
  if (/not authenticated|unauthori[sz]ed|forbidden|\b40[13]\b|sign[ -]?in/i.test(message)) return 'auth'
  if (/\b[45]\d\d\b|internal server|bad gateway|service unavailable/i.test(message)) return 'server'
  if (/timed? ?out|timeout|deadline|abort/i.test(message)) return 'timeout'
  if (/failed to fetch|networkerror|load failed|network request failed|err_/i.test(message)) return 'offline'
  return 'unknown'
}

const LOAD_FAILURE_COPY: Record<LoadFailureCause, { cause: string; action: string }> = {
  offline: {
    cause: 'This screen could not reach the server.',
    action: 'Check your internet connection, then select Try again.',
  },
  timeout: {
    cause: 'The server did not answer in time.',
    action: 'Select Try again in a moment.',
  },
  auth: {
    cause: 'This sign-in is no longer accepted by the server.',
    action: 'Sign in again, then select Try again.',
  },
  server: {
    cause: 'The server answered with an error.',
    action: 'Select Try again in a moment. If it keeps failing, the service is having trouble.',
  },
  unknown: {
    cause: 'The request did not complete.',
    action: 'Select Try again. If it keeps failing, reload the page.',
  },
}

/**
 * Copy for a failed read of the signed-in user's saved work.
 *
 * The two inputs are deliberately separate, because they can disagree. The
 * store nulls `userProjectsLoadedAt` on a principal change without clearing
 * `userProjects`, so a first read for a new principal can fail while the
 * previous list is still on screen. Taking only one of the two would then
 * produce a banner that contradicts what the reader can see.
 *
 * @param hasLoadedBefore whether a read has ever landed for this principal —
 *   i.e. `userProjectsLoadedAt !== null`.
 * @param listedCount how many items the screen is currently listing.
 */
export function describeSavedWorkLoadFailure(input: {
  message: string | null
  hasLoadedBefore: boolean
  listedCount: number
}): LoadFailureCopy {
  const { cause, action } = LOAD_FAILURE_COPY[classifyLoadFailure(input.message)]
  const isShowingList = input.listedCount > 0
  const state = isShowingList
    ? 'What is listed below is the last list that loaded, so it may be out of date.'
    : 'Nothing is listed below, and nothing has been deleted.'
  return {
    // "Updated" the moment there is any earlier result to update — whether it
    // is on screen now or was recorded as an empty list. Only a screen with
    // neither can honestly say the load never happened.
    title:
      input.hasLoadedBefore || isShowingList
        ? 'Saved work could not be updated'
        : 'Saved work could not be loaded',
    guidance: `${cause} ${state} ${action}`,
    technicalDetail: input.message?.trim() || null,
  }
}

/**
 * Copy for a failed read of the outputs list. There is no first-read/re-read
 * split here: the banner replaces the list outright, so nothing stale is ever
 * left on screen beside it.
 */
export function describeOutputsLoadFailure(message: string | null): LoadFailureCopy {
  const { cause, action } = LOAD_FAILURE_COPY[classifyLoadFailure(message)]
  return {
    title: 'Outputs could not be loaded',
    guidance: `${cause} ${action}`,
    technicalDetail: message?.trim() || null,
  }
}

export function AppsPage() {
  const { locale, strings } = useStudioLocale()
  const shareCopy = strings.share
  const workCopy = strings.work
  const outputsCopy = strings.outputsSection
  const [quickName, setQuickName] = useState('')
  const [creating, setCreating] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ProjectMeta | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [renameTarget, setRenameTarget] = useState<ProjectMeta | null>(null)
  const [renameName, setRenameName] = useState('')
  const [renaming, setRenaming] = useState(false)

  // Deployed apps state
  const [deployedApps, setDeployedApps] = useState<DeployedAppInfo[]>([])
  const [loadingDeployed, setLoadingDeployed] = useState(false)
  const [deployedError, setDeployedError] = useState<string | null>(null)
  const [deployedAppsReadSucceeded, setDeployedAppsReadSucceeded] = useState(false)
  const [visibleProjectCount, setVisibleProjectCount] = useState(PROJECT_BATCH_SIZE)
  const [visibleDeployedCount, setVisibleDeployedCount] = useState(PROJECT_BATCH_SIZE)
  const [settingsTarget, setSettingsTarget] = useState<DeployedAppInfo | null>(null)
  const [newSlug, setNewSlug] = useState('')
  const [savingSettings, setSavingSettings] = useState(false)
  const [deployDeleteTarget, setDeployDeleteTarget] = useState<DeployedAppInfo | null>(null)
  const [deletingDeployed, setDeletingDeployed] = useState(false)
  const [analyticsSlug, setAnalyticsSlug] = useState<string | null>(null)
  const [upgradeTarget, setUpgradeTarget] = useState<DeployedAppInfo | null>(null)
  const [upgradingSlug, setUpgradingSlug] = useState<string | null>(null)
  const [selectedOutputId, setSelectedOutputId] = useState<string | null>(null)

  const projectList = useStudioStore((s) => s.projectList)
  const userProjects = useStudioStore((s) => s.userProjects)
  const isLoadingUserProjects = useStudioStore((s) => s.isLoadingUserProjects)
  const userProjectsError = useStudioStore((s) => s.userProjectsError)
  // Null until a list has landed for the signed-in principal — the store resets
  // it on a principal change. This is what separates "never read" from "read
  // before, re-read failed" in the failure copy below.
  const userProjectsLoadedAt = useStudioStore((s) => s.userProjectsLoadedAt)
  const initProjectList = useStudioStore((s) => s.initProjectList)
  const openServerProject = useStudioStore((s) => s.openServerProject)
  const loadUserProjects = useStudioStore((s) => s.loadUserProjects)
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem)
  const setStudioMode = useStudioStore((s) => s.setStudioMode)
  const setViewMode = useStudioStore((s) => s.setViewMode)
  const createServerProject = useStudioStore((s) => s.createServerProject)
  const deleteProject = useStudioStore((s) => s.deleteProject)
  const duplicateProject = useStudioStore((s) => s.duplicateProject)
  const user = useStudioStore((s) => s.user)
  const studioRoute = useStudioStore((s) => s.studioRoute)
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)
  const isAppsLiveRoute = studioRoute === 'apps-live'
  const visibleProjects = userProjects.slice(0, visibleProjectCount)
  const visibleDeployedApps = deployedApps.slice(0, visibleDeployedCount)
  const cogoXyzLibraryItems = cogoXyzRegistry.listLibraryItems({ includeArchived: true }).items
  const activeLibraryItems = cogoXyzLibraryItems.filter(
    (item) => item.workItem.status !== 'archived',
  )
  const projectById = useMemo(() => {
    return new Map(userProjects.map((project) => [project.id, project]))
  }, [userProjects])
  const projectIds = new Set(projectById.keys())
  const libraryItemCountsByProject = new Map<string, number>()
  for (const item of activeLibraryItems) {
    const projectId = libraryItemSourceProjectId(item, projectIds)
    if (!projectId) continue
    libraryItemCountsByProject.set(
      projectId,
      (libraryItemCountsByProject.get(projectId) ?? 0) + 1,
    )
  }
  const proofHealthForOutput = (id: string) =>
    getProjectProofHealth({ locale, strings }, projectById.get(id))
  const liveApps = deployedApps.filter(
    (app) => app.status === 'live' && proofHealthForOutput(app.id).status === 'verified',
  )
  const unverifiedLiveApps = deployedApps.filter(
    (app) => app.status === 'live' && proofHealthForOutput(app.id).status !== 'verified',
  )
  const draftApps = deployedApps.filter((app) => app.status !== 'live')
  const selectedOutput = selectedOutputId === null
    ? deployedApps[0] ?? null
    : deployedApps.find((app) => app.id === selectedOutputId) ?? null
  const selectedOutputProofHealth = selectedOutput
    ? getProjectProofHealth({ locale, strings }, projectById.get(selectedOutput.id))
    : null
  const deployedAppById = useMemo(() => {
    return new Map(deployedApps.map((app) => [app.id, app]))
  }, [deployedApps])
  // One read of the readiness records, feeding both the "Ready checks" tile
  // and the Share health panel — so the tile and the panel cannot disagree.
  const sharedOutputProof = liveApps.map((app) =>
    getProjectProofHealth({ locale, strings }, projectById.get(app.id)),
  )
  const shareHealth = summarizeShareHealth(sharedOutputProof, shareCopy.health)
  const savedWorkFailure = userProjectsError
    ? describeSavedWorkLoadFailure({
        message: userProjectsError,
        hasLoadedBefore: userProjectsLoadedAt !== null,
        listedCount: userProjects.length,
      })
    : null
  const outputsFailure = deployedError ? describeOutputsLoadFailure(deployedError) : null
  const verifiedDeployedCount = shareHealth.checkedCount
  const sharedOutputLinks = liveApps
    .filter((app): app is DeployedAppInfo & { subdomain: string } => Boolean(app.subdomain))
    .slice(0, 3)
  const latestDeployment = deployedApps
    .map((app) => new Date(app.updatedAt).getTime())
    .filter(Number.isFinite)
    .sort((a, b) => b - a)[0]
  const agentShareCopy = AGENT_SHARE_COPY[locale]
  const workLibraryCopy = WORK_LIBRARY_RELATIONSHIP_COPY[locale]
  const projectRelationshipsReady =
    userProjectsLoadedAt !== null &&
    !isLoadingUserProjects &&
    !userProjectsError

  function navigateTo(route: 'import' | 'library', query?: string) {
    setStudioRoute(route)
    if (query && typeof window !== 'undefined') {
      window.history.pushState(null, '', `/studio/${route}?q=${encodeURIComponent(query)}`)
      window.dispatchEvent(new PopStateEvent('popstate'))
      return
    }
    pushStudioRoute(route)
  }

  function navigateToNewProject() {
    pushStudioPath('/studio/new')
  }

  function openShareOverviewForProject(projectId: string) {
    setSelectedOutputId(projectId)
    setStudioRoute('apps-live')
    pushStudioRoute('apps-live')
  }

  async function openDeployForProject(projectId: string) {
    await openServerProject(projectId)
    setStudioMode('edit')
    setViewMode('editor')
    setActiveRailItem('deploy')
  }

  async function openProjectWithChat(project: Pick<ProjectMeta, 'id' | 'name'>) {
    await openServerProject(project.id)
    openStudioLaunchChat({
      source: 'project-open',
      projectName: project.name,
    })
  }

  async function openDeployedAppWithChat(app: DeployedAppInfo) {
    await openServerProject(app.id)
    openStudioLaunchChat({
      source: 'project-open',
      projectName: app.name,
    })
  }

  async function askCoogoAboutShareReadiness(app: DeployedAppInfo) {
    const proofHealth = getProjectProofHealth({ locale, strings }, projectById.get(app.id))
    const request = agentShareCopy.askNotice(app.name, proofHealth.label, proofHealth.detail)
    await openServerProject(app.id)
    openStudioLaunchChat({
      source: 'project-open',
      projectName: app.name,
    })
    const store = useStudioStore.getState()
    store.setPendingChatNotice(request)
    store.setPendingChatMessage(request)
  }

  useEffect(() => {
    if (projectList.length === 0) initProjectList()
  }, [projectList.length, initProjectList])

  // Keyed on the user ID, not the user OBJECT: `onStudioAuthStateChange`
  // hands the store a fresh user instance on every auth event (initial
  // session, sign-in, token refresh), and depending on the object re-ran this
  // effect — and re-read `/api/projects` — once per event.
  const userId = user?.id ?? null
  useEffect(() => {
    if (userId) void loadUserProjects()
  }, [userId, loadUserProjects])

  const loadDeployedApps = useCallback(async () => {
    setLoadingDeployed(true)
    setDeployedError(null)
    setDeployedAppsReadSucceeded(false)
    try {
      setDeployedApps(await fetchDeployedApps())
      setDeployedAppsReadSucceeded(true)
    } catch (error) {
      console.error('[apps] Failed to load shared outputs:', error)
      setDeployedError(
        error instanceof Error && error.message
          ? error.message
          : 'Could not load shared outputs.',
      )
    } finally {
      setLoadingDeployed(false)
    }
  }, [])

  useEffect(() => {
    void loadDeployedApps()
  }, [loadDeployedApps])

  useEffect(() => {
    setVisibleProjectCount(PROJECT_BATCH_SIZE)
  }, [userProjects.length])

  useEffect(() => {
    setVisibleDeployedCount(PROJECT_BATCH_SIZE)
  }, [deployedApps.length, isAppsLiveRoute])

  useEffect(() => {
    if (!deleteTarget && !renameTarget && !settingsTarget && !upgradeTarget && !deployDeleteTarget) return
    const handleDialogKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDeleteTarget(null)
        setRenameTarget(null)
        setSettingsTarget(null)
        setUpgradeTarget(null)
        setDeployDeleteTarget(null)
        return
      }
      if (event.key !== 'Tab') return
      const dialog = document.querySelector<HTMLElement>('[role="dialog"][aria-modal="true"]')
      if (!dialog) return
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (!dialog.contains(document.activeElement)) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', handleDialogKeyDown)
    return () => window.removeEventListener('keydown', handleDialogKeyDown)
  }, [deleteTarget, deployDeleteTarget, renameTarget, settingsTarget, upgradeTarget])

  async function handleQuickCreate() {
    const name = quickName.trim()
    if (!name) return
    setCreating(true)
    try {
      await createServerProject(name)
    } catch (err) {
      console.error('[apps] Quick create failed:', err)
      // persona-ux-04 — shared handler: no double toast on a marked failure,
      // sign out on auth expiry, generic fallback otherwise.
      handleProjectCreateError(err, {
        toastError: (m) => toast.error(m),
        signOut: () => void useStudioStore.getState().signOut(),
      })
    } finally {
      setCreating(false)
      setQuickName('')
    }
  }

  async function handleDuplicate(project: ProjectMeta) {
    try {
      await duplicateProject(project.id, `${project.name} (copy)`)
      toast.success('Work duplicated')
    } catch {
      toast.error('Failed to duplicate work')
    }
  }

  async function handleRename() {
    if (!renameTarget || !renameName.trim()) return
    setRenaming(true)
    try {
      await saveProjectMeta(renameTarget.id, { name: renameName.trim() })
      void loadUserProjects({ force: true })
      toast.success('Work renamed')
      setRenameTarget(null)
    } catch {
      toast.error('Failed to rename work')
    } finally {
      setRenaming(false)
    }
  }

  function handleOpenSettings(app: DeployedAppInfo) {
    setSettingsTarget(app)
    setNewSlug(app.slug)
  }

  async function handleSaveSettings() {
    if (!settingsTarget || !newSlug.trim()) return
    setSavingSettings(true)
    try {
      await updateAppSettings(settingsTarget.slug, newSlug.trim())
      toast.success('Subdomain updated')
      setSettingsTarget(null)
      await loadDeployedApps()
    } catch (err: any) {
      toast.error(err.message || 'Failed to update settings')
    } finally {
      setSavingSettings(false)
    }
  }

  async function handleDeleteDeployed() {
    if (!deployDeleteTarget) return
    setDeletingDeployed(true)
    try {
      await deleteDeployedApp(deployDeleteTarget.slug)
      toast.success('Shared output removed')
      setDeployDeleteTarget(null)
      await loadDeployedApps()
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove shared output')
    } finally {
      setDeletingDeployed(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteProject(deleteTarget.id)
      toast.success('Work deleted')
      setDeleteTarget(null)
    } catch {
      toast.error('Failed to delete work')
    } finally {
      setDeleting(false)
    }
  }

  async function handleUpgradeConfirm() {
    if (!upgradeTarget) return
    const slug = upgradeTarget.slug
    setUpgradingSlug(slug)
    setUpgradeTarget(null)
    try {
      // 1. Fetch real deployed app data from API
      const appData = await fetchDeployedAppJson(slug)

      // 2. Run additive upgrade logic
      const result = upgradeToTierA(appData)
      if (!result.success) {
        toast.error(result.error || 'Upgrade logic failed')
        return
      }

      // 3. Persist upgraded data back to server
      await updateDeployedAppTier(slug, appData)

      toast.success('Output upgraded to Tier A')
      // Refresh deployed apps
      await loadDeployedApps()
    } catch (err: any) {
      toast.error(err.message || 'Upgrade failed. Your existing content is preserved. Please try again.')
    } finally {
      setUpgradingSlug(null)
    }
  }

  function evidenceMatchesDeployedApp(app: DeployedAppInfo): number {
    const probes = [app.id, app.slug, app.name, app.subdomain].filter((value): value is string => Boolean(value))
    return cogoXyzLibraryItems.filter((item) => {
      const haystack = JSON.stringify({
        title: item.workItem.title,
        sourceRef: item.workItem.sourceRef,
        bundle: item.bundle,
        preview: item.preview,
      }).toLowerCase()
      return probes.some((probe) => haystack.includes(probe.toLowerCase()))
    }).length
  }

  function evidenceQueryForDeployedApp(app: DeployedAppInfo): string {
    return app.slug || app.name || app.id
  }

  const launchChecklist = [
    {
      label: shareCopy.checklist.publicUrl,
      value: liveApps.length > 0
        ? fillShare(shareCopy.checklist.sharedCount, { count: String(liveApps.length) })
        : shareCopy.checklist.notShared,
      ready: liveApps.length > 0,
    },
    {
      label: shareCopy.checklist.readinessCheck,
      // "Needs a check" is only true of something that exists to be checked.
      // With nothing shared, the honest value is that there is nothing yet.
      value: liveApps.length === 0
        ? shareCopy.checklist.nothingSharedYet
        : verifiedDeployedCount > 0
          ? fillShare(shareCopy.checklist.readyCount, {
              count: String(verifiedDeployedCount),
            })
          : shareCopy.checklist.needsCheck,
      ready: verifiedDeployedCount > 0,
    },
    {
      label: shareCopy.checklist.outcomePath,
      value: shareCopy.checklist.outcomePathValue,
      ready: liveApps.length > 0,
    },
    {
      label: shareCopy.checklist.updateControl,
      value: shareCopy.checklist.updateControlValue,
      ready: deployedApps.length > 0,
    },
  ]

  return (
    <DashboardContainer className="py-6 space-y-8">
      <div>
        <h1 data-route-heading={isAppsLiveRoute ? 'apps-live' : 'projects'} className="text-2xl font-semibold text-foreground">{isAppsLiveRoute ? shareCopy.title : workCopy.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isAppsLiveRoute
            ? shareCopy.subtitle
            : workCopy.subtitle}
        </p>
        {!isAppsLiveRoute && projectRelationshipsReady ? (
          <div
            data-testid="projects-library-relationship-summary"
            className="mt-3 inline-flex max-w-3xl rounded-lg border border-border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground"
          >
            {workLibraryCopy.summary(userProjects.length, activeLibraryItems.length)}
          </div>
        ) : null}
      </div>

      {isAppsLiveRoute && (
        <section className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                {shareCopy.centerTitle}
              </h2>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                {shareCopy.centerBody}
              </p>
            </div>
            {deployedAppsReadSucceeded ? selectedOutput ? (
              <div
                data-testid="selected-output-actions"
                className="flex max-w-full flex-col items-start gap-2 lg:items-end"
              >
                <p className="max-w-full truncate text-xs text-muted-foreground">
                  {agentShareCopy.selected}: <span className="font-medium text-foreground">{selectedOutput.name}</span>
                </p>
                {isExternalCogoPublish(selectedOutput.subdomain) ? (
                  <p
                    data-testid="selected-output-external-publish-boundary"
                    className="max-w-xl text-xs leading-5 text-muted-foreground lg:text-right"
                  >
                    <span className="font-semibold text-foreground">{agentShareCopy.externalPublish}.</span>{' '}
                    {agentShareCopy.externalPublishBoundary}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {selectedOutput.status === 'live' &&
                  selectedOutputProofHealth?.status === 'verified' &&
                  selectedOutput.subdomain ? (
                    <>
                      <a
                        href={`https://${selectedOutput.subdomain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-10 items-center gap-2 rounded-md border border-border bg-foreground px-3 text-sm font-medium text-background transition-opacity hover:opacity-90"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {agentShareCopy.open}
                      </a>
                      <button
                        type="button"
                        onClick={() => handleOpenSettings(selectedOutput)}
                        className="inline-flex h-10 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                      >
                        <Settings className="h-4 w-4" />
                        {agentShareCopy.manage}
                      </button>
                      <button
                        type="button"
                        onClick={() => { void askCoogoAboutShareReadiness(selectedOutput) }}
                        className="inline-flex h-10 items-center gap-2 rounded-md border border-border bg-muted/40 px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                      >
                        <MessageCircle className="h-4 w-4" />
                        {agentShareCopy.ask}
                      </button>
                    </>
                  ) : selectedOutputProofHealth ? (
                    <button
                      type="button"
                      onClick={() => { void openDeployForProject(selectedOutput.id) }}
                      className="inline-flex h-10 items-center rounded-md border border-border bg-foreground px-3 text-sm font-medium text-background transition-opacity hover:opacity-90"
                    >
                      {agentShareCopy.nextAction}: {proofActionLabel(selectedOutputProofHealth.action, strings.proofHealth)}
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div data-testid="true-empty-output-actions" className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={navigateToNewProject}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-border bg-foreground px-3 text-sm font-medium text-background transition-colors hover:opacity-90"
                >
                  {shareCopy.createOutput}
                </button>
                <button
                  type="button"
                  onClick={() => navigateTo('import')}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  {shareCopy.addSource}
                </button>
                <button
                  type="button"
                  onClick={() => navigateTo('library')}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  {shareCopy.sources}
                </button>
              </div>
            ) : null}
          </div>

          <details
            data-testid="share-readiness-details"
            className="group rounded-lg border border-border bg-background"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 marker:content-none">
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-foreground">
                  {agentShareCopy.readinessDetails}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {agentShareCopy.readinessDetailsHint}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span
                  data-testid="share-health-badge"
                  className={`rounded-full border bg-background px-2 py-0.5 text-[11px] font-semibold ${shareHealthBadgeClassName(shareHealth.tone)}`}
                >
                  {shareHealth.badge}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" />
              </span>
            </summary>

            <div className="space-y-4 border-t border-border p-4">
              <section className="grid gap-3 md:grid-cols-4" aria-label={strings.a11y.shareReadinessChecklist}>
                {launchChecklist.map((item) => (
                  <div key={item.label} className="rounded-md border border-border bg-background p-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`h-2 w-2 rounded-full ${item.ready ? 'bg-foreground' : 'bg-muted-foreground/40'}`}
                        aria-hidden
                      />
                      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{item.label}</span>
                    </div>
                    <div className="mt-2 text-sm font-semibold text-foreground">{item.value}</div>
                  </div>
                ))}
              </section>

              <div className="grid gap-3 md:grid-cols-5">
                <div className="rounded-md border border-border bg-background p-3" data-testid="tile-shared-outputs">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{shareCopy.tiles.sharedOutputs}</div>
                  <div className="mt-1 text-2xl font-semibold text-foreground">{liveApps.length}</div>
                </div>
                <div className="rounded-md border border-border bg-background p-3" data-testid="tile-ready-checks">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{shareCopy.tiles.readyChecks}</div>
                  <div className="mt-1 text-2xl font-semibold text-foreground">{verifiedDeployedCount}</div>
                </div>
                <div className="rounded-md border border-border bg-background p-3" data-testid="tile-drafts">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{shareCopy.tiles.drafts}</div>
                  <div className="mt-1 text-2xl font-semibold text-foreground">{draftApps.length}</div>
                </div>
                <div className="rounded-md border border-border bg-background p-3" data-testid="tile-needs-verification">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{agentShareCopy.needsVerification}</div>
                  <div className="mt-1 text-2xl font-semibold text-foreground">{unverifiedLiveApps.length}</div>
                </div>
                <div className="rounded-md border border-border bg-background p-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{shareCopy.tiles.latestUpdate}</div>
                  <div className="mt-1 text-sm font-semibold text-foreground">
                    {latestDeployment
                      ? new Date(latestDeployment).toLocaleDateString(locale)
                      : shareCopy.tiles.noSharedOutput}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-background p-3" data-testid="share-health">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground">
                      {shareCopy.health.title}
                    </h3>
                    <p data-testid="share-health-detail" className="mt-1 max-w-3xl text-xs text-muted-foreground">
                      {shareHealth.detail}
                    </p>
                  </div>
                  <div className="grid gap-2 text-[11px] text-muted-foreground sm:grid-cols-2 lg:min-w-[360px]">
                    <div>
                      <span className="block font-medium text-foreground">{shareCopy.health.checked}</span>
                      <span data-testid="share-health-checked">
                        {shareHealth.sharedCount === 0
                          ? shareCopy.health.nothingShared
                          : shareHealth.checkedCount === 0
                            ? shareCopy.health.noneYet
                            : fillShare(shareCopy.health.checkedOfShared, {
                                checked: String(shareHealth.checkedCount),
                                shared: String(shareHealth.sharedCount),
                              })}
                      </span>
                    </div>
                    <div>
                      <span className="block font-medium text-foreground">{shareCopy.health.lastRecordedCheck}</span>
                      <span data-testid="share-health-last-checked">
                        {shareHealth.lastCheckedAt
                          ? formatShareDate(shareHealth.lastCheckedAt)
                          : shareCopy.health.never}
                      </span>
                    </div>
                    <div>
                      <span className="block font-medium text-foreground">{shareCopy.health.needsAttention}</span>
                      {shareHealth.blockedCount > 0
                        ? fillShare(shareCopy.health.blockedCount, { count: String(shareHealth.blockedCount) })
                        : shareCopy.health.none}
                    </div>
                    <div>
                      <span className="block font-medium text-foreground">{shareCopy.health.draftsNotShared}</span>
                      {draftApps.length}
                    </div>
                  </div>
                </div>
                {sharedOutputLinks.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {sharedOutputLinks.map((app) => (
                        <a
                          key={app.id}
                          href={`https://${app.subdomain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={app.subdomain}
                          className="inline-flex h-10 max-w-full items-center gap-1 rounded-md border border-border px-3 text-[11px] font-medium text-foreground transition-colors hover:bg-muted"
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          <span className="truncate">{agentShareCopy.openNamed(app.name)}</span>
                        </a>
                      ))}
                    </div>
                    {sharedOutputLinks.some((app) => isExternalCogoPublish(app.subdomain)) ? (
                      <p className="text-[11px] leading-4 text-muted-foreground">
                        <span className="font-semibold text-foreground">{agentShareCopy.externalPublish}.</span>{' '}
                        {agentShareCopy.externalPublishBoundary}
                      </p>
                    ) : null}
                  </div>
                )}
              </div>

              {visibleDeployedApps.length > 0 && (
                <section className="space-y-2" aria-label={agentShareCopy.outputDetails}>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {agentShareCopy.outputDetails}
                  </h3>
                  {visibleDeployedApps.map((app) => {
                    const proofHealth = getProjectProofHealth({ locale, strings }, projectById.get(app.id))
                    const evidenceMatchCount = evidenceMatchesDeployedApp(app)
                    return (
                      <div key={app.id} className="rounded-lg border border-border bg-card p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm font-medium text-foreground">{app.name}</span>
                          <span className="text-[10px] font-medium text-muted-foreground">
                            {app.status === 'live'
                              ? proofHealth.status === 'verified'
                                ? agentShareCopy.sharedStatus
                                : agentShareCopy.needsVerificationStatus
                              : agentShareCopy.draftStatus}
                          </span>
                        </div>
                        <div className="mt-3 grid gap-2 text-[11px] text-muted-foreground md:grid-cols-2 xl:grid-cols-4">
                          <div>
                            <span className="block font-medium text-foreground">{agentShareCopy.sharedSurface}</span>
                            {app.status === 'live' && proofHealth.status === 'verified'
                              ? agentShareCopy.publicOutput
                              : app.status === 'live'
                                ? agentShareCopy.needsVerificationStatus
                                : agentShareCopy.draftOutput}
                          </div>
                          <div>
                            <span className="block font-medium text-foreground">{agentShareCopy.version}</span>
                            v{app.version}{app.liveVersion != null && proofHealth.status === 'verified' && ` ${agentShareCopy.sharedVersion(app.liveVersion)}`}
                          </div>
                          <div>
                            <span className="block font-medium text-foreground">{agentShareCopy.source}</span>
                            {agentShareCopy.sourceBundle}
                          </div>
                          <div>
                            <span className="block font-medium text-foreground">{agentShareCopy.readiness}</span>
                            <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${proofHealthBadgeClassName(proofHealth.tone)}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${proofHealthDotClassName(proofHealth.tone)}`} aria-hidden />
                              {proofHealth.label}
                            </span>
                            <span className="mt-1 block">{proofHealth.detail}</span>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {proofHealth.status !== 'verified' && (
                            <button
                              type="button"
                              onClick={() => { void openDeployForProject(app.id) }}
                              className="inline-flex min-h-10 items-center rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
                            >
                              {agentShareCopy.nextAction}: {proofActionLabel(proofHealth.action, strings.proofHealth)}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => navigateTo('library', evidenceQueryForDeployedApp(app))}
                            className="inline-flex min-h-10 items-center rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
                          >
                            {agentShareCopy.savedRecords}: {evidenceMatchCount > 0 ? evidenceMatchCount : '0'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { void openDeployedAppWithChat(app) }}
                            className="inline-flex min-h-10 items-center rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted"
                          >
                            {agentShareCopy.openInStudio}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenSettings(app)}
                            className="inline-flex min-h-10 items-center rounded-md border border-border px-3 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            {agentShareCopy.manage}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeployDeleteTarget(app)}
                            className="inline-flex min-h-10 items-center rounded-md border border-border px-3 text-xs text-destructive hover:bg-destructive/10"
                          >
                            {agentShareCopy.remove}
                          </button>
                          {app.tier === 'B' && (
                            <button
                              type="button"
                              onClick={() => setUpgradeTarget(app)}
                              disabled={upgradingSlug === app.slug}
                              className="inline-flex min-h-10 items-center rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
                            >
                              {upgradingSlug === app.slug ? agentShareCopy.upgrading : agentShareCopy.upgrade}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setAnalyticsSlug(analyticsSlug === app.slug ? null : app.slug)}
                            className="inline-flex min-h-10 items-center gap-1 rounded-md border border-border px-3 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            {analyticsSlug === app.slug ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            {agentShareCopy.analytics}
                          </button>
                        </div>
                        {analyticsSlug === app.slug && (
                          <div className="mt-3">
                            <AppAnalytics slug={app.slug} appName={app.name} />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </section>
              )}
            </div>
          </details>
        </section>
      )}

      {/* Quick Create */}
      {!isAppsLiveRoute && <div className="flex flex-col gap-2 lg:flex-row">
        <input
          data-testid="dashboard-quick-create-name"
          type="text"
          value={quickName}
          onChange={(e) => setQuickName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleQuickCreate() }}
          placeholder={workCopy.namePlaceholder}
          className="flex-1 px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          disabled={creating}
        />
        <button
          data-testid="dashboard-quick-create-submit"
          onClick={handleQuickCreate}
          disabled={creating || !quickName.trim()}
          className="px-5 py-3 rounded-lg border border-border bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {creating ? `${workCopy.create}...` : workCopy.create}
        </button>
        <button
          onClick={() => {
            setStudioRoute('templates')
            pushStudioRoute('templates')
          }}
          className="px-5 py-3 rounded-lg border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors"
        >
          {workCopy.browseStarters}
        </button>
        <button
          type="button"
          onClick={() => navigateTo('import')}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-5 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <UploadCloud className="h-4 w-4" />
          {workCopy.importSource}
        </button>
      </div>}

      {/* App Grid */}
      {!isAppsLiveRoute && <div className="space-y-3">
        {savedWorkFailure && (
          <div
            role="alert"
            data-testid="saved-work-error"
            className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-start sm:justify-between"
          >
            <div>
              <p className="text-sm font-medium text-foreground">{savedWorkFailure.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">{savedWorkFailure.guidance}</p>
              {savedWorkFailure.technicalDetail && (
                <p className="mt-1 text-[11px] text-muted-foreground/70">
                  Technical detail: {savedWorkFailure.technicalDetail}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => { void loadUserProjects({ force: true }) }}
              className="inline-flex h-11 items-center justify-center rounded-lg border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-muted"
            >
              Try again
            </button>
          </div>
        )}
        {isLoadingUserProjects && userProjects.length === 0 ? (
          <div
            role="status"
            aria-live="polite"
            className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground"
          >
            Loading saved work…
          </div>
        ) : userProjects.length > 0 ? (
          <>
            <div
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
              aria-busy={isLoadingUserProjects}
            >
              {visibleProjects.map((p) => {
                const deployedApp = deployedAppById.get(p.id)
                const savedItemCount = libraryItemCountsByProject.get(p.id) ?? 0
                return (
                  <ProjectCardStatusFrame
                    key={p.id}
                    relationship={projectRelationshipsReady ? (
                      <>
                        <span>{workLibraryCopy.editableWorkspace}</span>
                        <span className="font-medium text-foreground">
                          {savedItemCount > 0
                            ? workLibraryCopy.savedItems(savedItemCount)
                            : workLibraryCopy.notSaved}
                        </span>
                      </>
                    ) : undefined}
                  >
                    <AppCard
                      project={p}
                      onOpen={() => { void openProjectWithChat(p) }}
                      onDuplicate={handleDuplicate}
                      onDelete={setDeleteTarget}
                      onRename={(project) => { setRenameTarget(project); setRenameName(project.name) }}
                      onSettings={deployedApp ? () => handleOpenSettings(deployedApp) : undefined}
                      onShare={() => openShareOverviewForProject(p.id)}
                    />
                  </ProjectCardStatusFrame>
                )
              })}
            </div>
            {visibleProjectCount < userProjects.length && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={() => setVisibleProjectCount((count) => count + PROJECT_BATCH_SIZE)}
                  className="inline-flex h-11 items-center rounded-lg border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-muted"
                >
                  Show {Math.min(PROJECT_BATCH_SIZE, userProjects.length - visibleProjectCount)} more projects
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 space-y-3">
            <Folder className="w-10 h-10 text-muted-foreground/30 mx-auto" />
            <div>
              <p className="text-sm font-medium text-foreground">{workCopy.emptyTitle}</p>
              <p className="text-xs text-muted-foreground mt-1">{workCopy.emptyBody}</p>
            </div>
          </div>
        )}
      </div>}

      {/* ── Outputs ──────────────────────────────────────────────────────────
          Reported defect: this section was headed "Shared outputs" while it
          listed `deployedApps` — every output, drafts included. With 0 live and
          73 drafts the screen said "SHARED OUTPUTS 0" in the tile and then
          rendered 24 cards under a heading with the same words. The tile was
          right; the heading was the lie. The heading now names what the list
          holds and carries the same verified, draft, and needs-verification
          counts the tiles do, so the two agree
          by construction rather than by coincidence.

          FINDING-005: for a brand-new account (no work AND no outputs) this
          section only repeated the empty state directly above it — two boxes
          saying the same "nothing here yet". It stays for loading/error (a
          hidden failed read would be a lie) and once anything exists. */}
      {isAppsLiveRoute && (loadingDeployed || outputsFailure || deployedApps.length > 0 || userProjects.length > 0) ? (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Globe className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">{outputsCopy.heading}</h2>
          {!loadingDeployed && !deployedError && deployedApps.length > 0 && (
            <span
              data-testid="outputs-section-breakdown"
              className="text-xs text-muted-foreground"
            >
              {outputsCopy.breakdown(liveApps.length, draftApps.length)} · {agentShareCopy.needsVerificationCount(unverifiedLiveApps.length)}
            </span>
          )}
        </div>
        {loadingDeployed ? (
          <p role="status" aria-live="polite" className="py-4 text-sm text-muted-foreground">{outputsCopy.loading}</p>
        ) : outputsFailure ? (
          <div
            role="alert"
            data-testid="outputs-error"
            className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 sm:flex-row sm:items-start sm:justify-between"
          >
            <div>
              <p className="text-sm font-medium text-foreground">{outputsFailure.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">{outputsFailure.guidance}</p>
              {outputsFailure.technicalDetail && (
                <p className="mt-1 text-[11px] text-muted-foreground/70">
                  Technical detail: {outputsFailure.technicalDetail}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => { void loadDeployedApps() }}
              className="inline-flex h-11 items-center justify-center rounded-lg border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-muted"
            >
              Try again
            </button>
          </div>
        ) : deployedApps.length > 0 ? (
          <>
          <div className={isAppsLiveRoute ? 'grid gap-4 xl:grid-cols-2' : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'}>
            {visibleDeployedApps.map((app) => {
              const proofHealth = getProjectProofHealth({ locale, strings }, projectById.get(app.id))
              return (
                <div
                  key={app.id}
                  className={`relative rounded-xl border bg-card p-4 text-left transition-colors hover:border-foreground/20 hover:bg-muted/30 group ${
                    isAppsLiveRoute && selectedOutput?.id === app.id
                      ? 'border-foreground/40 ring-1 ring-foreground/10'
                      : 'border-border'
                  }`}
                >
                {/* Thumbnail */}
                <AppThumbnailPreview
                  title={app.name}
                  imageSrc={app.thumbnail}
                  projectId={app.id}
                  kind="project"
                  className="mb-3"
                />

                {/* Name + status — `min-w-0` + `title` for the same reason as
                    `app-card.tsx`: the ellipsis must not be the only copy of
                    the name. */}
                <div className="flex items-start justify-between">
                  <h3
                    data-testid="deployed-app-name"
                    className="min-w-0 text-sm font-medium text-foreground truncate pr-8"
                    title={app.name}
                  >
                    {app.name}
                  </h3>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-border bg-background font-medium shrink-0 flex items-center gap-1 text-muted-foreground">
                    <span className={`w-1.5 h-1.5 rounded-full ${app.status === 'live' && proofHealth.status === 'verified' ? 'bg-foreground' : 'bg-muted-foreground/40'}`} />
                    {app.status === 'live'
                      ? proofHealth.status === 'verified'
                        ? agentShareCopy.sharedStatus
                        : agentShareCopy.needsVerificationStatus
                      : agentShareCopy.draftStatus}
                  </span>
                </div>

                {/* Tier badge */}
                {!isAppsLiveRoute && app.tier && (
                  <span className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded border border-border bg-background text-muted-foreground font-medium">
                    {app.tier}
                  </span>
                )}

                {/* Version + date */}
                {!isAppsLiveRoute && (
                  <p className="mt-2 text-[10px] text-muted-foreground">
                    v{app.version}{app.liveVersion != null && proofHealth.status === 'verified' && ` (shared: v${app.liveVersion})`} · {new Date(app.updatedAt).toLocaleDateString('ko-KR')}
                  </p>
                )}

                {/* Subdomain link */}
                {app.status === 'live' && proofHealth.status === 'verified' && app.subdomain && (
                  <div className="mt-1">
                    <a
                      href={`https://${app.subdomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-10 max-w-full items-center gap-1 text-[10px] text-foreground hover:underline"
                      title={app.subdomain}
                    >
                      <ExternalLink className="w-3 h-3 shrink-0" />
                      <span className="truncate">{app.subdomain}</span>
                    </a>
                    {isExternalCogoPublish(app.subdomain) ? (
                      <p className="text-[10px] leading-4 text-muted-foreground">
                        {agentShareCopy.externalPublish} · {agentShareCopy.externalPublishBoundary}
                      </p>
                    ) : null}
                  </div>
                )}

                {isAppsLiveRoute && proofHealth.status !== 'verified' && (
                  <p
                    className={`mt-2 truncate text-xs ${
                      proofHealth.tone === 'blocked' ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                    title={proofHealth.detail}
                  >
                    <span className="font-medium">{agentShareCopy.blocker}:</span> {proofHealth.detail}
                  </p>
                )}

                {isAppsLiveRoute && (
                  <button
                    type="button"
                    onClick={() => setSelectedOutputId(app.id)}
                    aria-pressed={selectedOutput?.id === app.id}
                    className={`mt-3 inline-flex h-10 w-full items-center justify-center rounded-md border px-3 text-xs font-medium transition-colors ${
                      selectedOutput?.id === app.id
                        ? 'border-foreground/30 bg-muted text-foreground'
                        : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {selectedOutput?.id === app.id ? agentShareCopy.selectedLabel : agentShareCopy.select}
                  </button>
                )}

                {/* Share keeps management in the readiness disclosure. Work
                    keeps the existing direct action grid. */}
                {!isAppsLiveRoute && <div className="mt-3 grid grid-cols-2 gap-1">
                  <button
                    onClick={() => { void openDeployedAppWithChat(app) }}
                    className="inline-flex h-10 items-center justify-center gap-1 rounded border border-border px-2 text-[10px] text-foreground transition-colors hover:bg-muted"
                  >
                    <Pencil className="w-3 h-3" />
                    {strings.library.actions.edit.label}
                  </button>
                  <button
                    onClick={() => handleOpenSettings(app)}
                    className="h-10 rounded border border-border px-2 text-[10px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    {strings.navigation.settings}
                  </button>
                  <button
                    onClick={() => setDeployDeleteTarget(app)}
                    className="h-10 rounded border border-border px-2 text-[10px] text-destructive transition-colors hover:bg-destructive/10"
                  >
                    Remove
                  </button>
                  {app.tier === 'B' && (
                    <button
                      onClick={() => setUpgradeTarget(app)}
                      disabled={upgradingSlug === app.slug}
                      className="h-10 rounded border border-border px-2 text-[10px] font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
                    >
                      {upgradingSlug === app.slug ? 'Upgrading...' : 'Upgrade plan'}
                    </button>
                  )}
                  <button
                    onClick={() => setAnalyticsSlug(analyticsSlug === app.slug ? null : app.slug)}
                    className="inline-flex h-10 items-center justify-center gap-1 rounded border border-border px-2 text-[10px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    aria-label={strings.navigation.analytics}
                  >
                    {analyticsSlug === app.slug ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {strings.navigation.analytics}
                  </button>
                </div>}

                {/* Collapsible Analytics */}
                {!isAppsLiveRoute && analyticsSlug === app.slug && (
                  <AppAnalytics slug={app.slug} appName={app.name} />
                )}
                </div>
              )
            })}
          </div>
          {visibleDeployedCount < deployedApps.length && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => setVisibleDeployedCount((count) => count + PROJECT_BATCH_SIZE)}
                className="inline-flex h-11 items-center rounded-lg border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-muted"
              >
                {outputsCopy.showMore(Math.min(PROJECT_BATCH_SIZE, deployedApps.length - visibleDeployedCount))}
              </button>
            </div>
          )}
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-card p-6 text-center">
            <p className="text-sm font-medium text-foreground">{outputsCopy.emptyTitle}</p>
            <p className="mt-1 text-sm text-muted-foreground">{outputsCopy.emptyBody}</p>
          </div>
        )}
      </div>
      ) : null}



      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteTarget(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="delete-work-title" className="bg-background border border-border rounded-xl p-6 w-full max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h2 id="delete-work-title" className="text-lg font-semibold text-foreground mb-2">Delete Work</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to delete <span className="font-medium text-foreground">{deleteTarget.name}</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button autoFocus onClick={() => setDeleteTarget(null)} className="h-11 px-4 rounded-lg text-sm text-muted-foreground hover:text-foreground">{strings.settings.profile.cancel}</button>
              <button onClick={handleDelete} disabled={deleting} className="h-11 px-4 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {deleting ? 'Deleting...' : strings.projectCard.delete}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {renameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setRenameTarget(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="rename-work-title" className="bg-background border border-border rounded-xl p-6 w-full max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h2 id="rename-work-title" className="text-lg font-semibold text-foreground mb-2">Rename Work</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Enter a new name for <span className="font-medium text-foreground">{renameTarget.name}</span>.
            </p>
            <input
              type="text"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRename() }}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRenameTarget(null)} className="h-11 px-4 rounded-lg text-sm text-muted-foreground hover:text-foreground">{strings.settings.profile.cancel}</button>
              <button onClick={handleRename} disabled={renaming || !renameName.trim() || renameName.trim() === renameTarget.name} className="h-11 px-4 rounded-lg border border-border bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {renaming ? 'Renaming...' : strings.projectCard.rename}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {settingsTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSettingsTarget(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="share-settings-title" className="bg-background border border-border rounded-xl p-6 w-full max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h2 id="share-settings-title" className="text-lg font-semibold text-foreground mb-2">{shareCopy.settingsDialog.title}</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {fillShare(shareCopy.settingsDialog.body, { name: settingsTarget.name })}
            </p>
            <label htmlFor="share-settings-subdomain" className="block text-sm font-medium text-foreground mb-1">{shareCopy.settingsDialog.subdomainLabel}</label>
            <div className="flex items-center gap-1 mb-4">
              <input
                id="share-settings-subdomain"
                type="text"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="my-work"
                autoFocus
              />
              <span className="text-sm text-muted-foreground">.cogo.xyz</span>
            </div>
            <p className="mb-4 text-xs leading-5 text-muted-foreground">
              <span className="font-semibold text-foreground">{agentShareCopy.externalPublish}.</span>{' '}
              {agentShareCopy.externalPublishBoundary}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSettingsTarget(null)} className="h-11 px-4 rounded-lg text-sm text-muted-foreground hover:text-foreground">{strings.settings.profile.cancel}</button>
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings || !newSlug.trim() || newSlug === settingsTarget.slug}
                className="h-11 px-4 rounded-lg border border-border bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {savingSettings ? strings.settings.profile.saving : strings.settings.profile.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Confirmation */}
      {upgradeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setUpgradeTarget(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="upgrade-plan-title" className="bg-background border border-border rounded-xl p-6 w-full max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h2 id="upgrade-plan-title" className="text-lg font-semibold text-foreground mb-2">Upgrade plan</h2>
            <p className="text-sm text-muted-foreground mb-4">
              This will add publishing, auth, and payment features to this output. Your existing content will be preserved.
            </p>
            <div className="flex justify-end gap-2">
              <button autoFocus onClick={() => setUpgradeTarget(null)} className="h-11 px-4 rounded-lg text-sm text-muted-foreground hover:text-foreground">{strings.settings.profile.cancel}</button>
              <button onClick={handleUpgradeConfirm} className="h-11 px-4 rounded-lg border border-border bg-foreground text-background text-sm font-medium hover:opacity-90">
                Upgrade
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared-output delete confirmation */}
      {deployDeleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeployDeleteTarget(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="remove-shared-output-title" className="bg-background border border-border rounded-xl p-6 w-full max-w-sm shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h2 id="remove-shared-output-title" className="text-lg font-semibold text-foreground mb-2">Remove Shared Output</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to remove the shared output for <span className="font-medium text-foreground">{deployDeleteTarget.name}</span>?
              This will delete the public files and the subdomain <span className="font-medium text-foreground">{deployDeleteTarget.subdomain}</span> will no longer be accessible.
            </p>
            <div className="flex justify-end gap-2">
              <button autoFocus onClick={() => setDeployDeleteTarget(null)} className="h-11 px-4 rounded-lg text-sm text-muted-foreground hover:text-foreground">{strings.settings.profile.cancel}</button>
              <button onClick={handleDeleteDeployed} disabled={deletingDeployed} className="h-11 px-4 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {deletingDeployed ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

    </DashboardContainer>
  )
}
